import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import PopupMultiSelectField from "./PopupMultiSelectField";
import { fetchRecruiterUsers } from "../utils/teamMembers";
import FigmaIcon from "./icons/FigmaIcon";

const PURPLE = "#9C4FFF";
const BLUE = "#3451F4";
const BORDER = "#D0D5DD";
const MUTED = "#98A2B3";
const DEFAULT_ROLE_OPTIONS = [
  "Recruiter",
  "Team Manager",
  "HR Manager",
  "Hiring Manager",
  "Coordinator",
  "Talent Acquisition",
];
const EMPTY_LIST = [];

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    minHeight: 48,
    borderRadius: "12px",
    backgroundColor: "#FFFFFF",
    boxShadow: "0 1px 2px rgba(16, 24, 40, 0.06)",
    "& fieldset": { borderColor: BORDER },
    "&:hover fieldset": { borderColor: "#CBD5E1" },
    "&.Mui-focused fieldset": { borderColor: PURPLE, borderWidth: 2 },
  },
  "& .MuiInputBase-input": {
    fontSize: 13,
    fontFamily: "Be Vietnam Pro",
    color: "#475467",
  },
  "& .MuiInputBase-input::placeholder": {
    color: MUTED,
    opacity: 1,
  },
};

const chipSx = {
  height: 30,
  borderRadius: "999px",
  backgroundColor: "#EAF2FF",
  color: "#3451F4",
  fontWeight: 500,
  fontSize: 13,
  fontFamily: "Be Vietnam Pro",
  "& .MuiChip-label": {
    px: 1.35,
  },
  "& .MuiChip-deleteIcon": {
    color: "#3451F4",
    fontSize: 18,
  },
};

const normalizeText = (value) => String(value || "").replace(/\s+/g, " ").trim();
const normalizeTextKey = (value) => normalizeText(value).toLowerCase();

const uniqText = (values = []) =>
  Array.from(
    new Set(
      (Array.isArray(values) ? values : [])
        .map((value) => normalizeText(value))
        .filter(Boolean)
    )
  );

const areTextArraysEqual = (left = [], right = []) => {
  if (left === right) return true;
  if (!Array.isArray(left) || !Array.isArray(right)) return false;
  if (left.length !== right.length) return false;

  for (let index = 0; index < left.length; index += 1) {
    if (left[index] !== right[index]) return false;
  }

  return true;
};

function Label({ children }) {
  return (
    <Typography data-popup-label
      sx={{
        mb: 0.75,
        fontSize: 13,
        fontWeight: 600,
        color: "#344054",
        fontFamily: "Be Vietnam Pro",
      }}
    >
      {children}
    </Typography>
  );
}

export default function JobOwnerDialog({
  open,
  onClose,
  onSubmit,
  apiBase,
  resetKey,
  summaryItems = EMPTY_LIST,
  defaultOwnerNames = EMPTY_LIST,
  defaultOwnerRoles = EMPTY_LIST,
  nameSuggestions = EMPTY_LIST,
  roleSuggestions = EMPTY_LIST,
  title = "Add Owner",
  sectionTitle = "Details",
  submitLabel = "Add New Owner",
  nameLabel = "Name",
  roleLabel = "Role",
  namePlaceholder = "Select owner",
  rolePlaceholder = "Select role",
  roleChipsBelow = true,
  nameLoadErrorText = "Failed to load owners.",
  requiredNameErrorText = "Select an owner name to continue.",
  requiredRoleErrorText = "Select an owner role to continue.",
  loadRecruiterNameOptions = true,
  headerIcon = null,
}) {
  const lastInitializedKeyRef = useRef("");
  const recruiterUsersRef = useRef([]);
  const normalizedSummaryItems = useMemo(() => uniqText(summaryItems), [summaryItems]);
  const normalizedDefaultOwnerNames = useMemo(
    () => uniqText(defaultOwnerNames).slice(-1),
    [defaultOwnerNames]
  );
  const normalizedDefaultOwnerRoles = useMemo(
    () => uniqText(defaultOwnerRoles).slice(-1),
    [defaultOwnerRoles]
  );
  const normalizedNameSuggestions = useMemo(() => uniqText(nameSuggestions), [nameSuggestions]);
  const normalizedRoleSuggestions = useMemo(
    () => uniqText([...DEFAULT_ROLE_OPTIONS, ...roleSuggestions, ...defaultOwnerRoles]),
    [defaultOwnerRoles, roleSuggestions]
  );

  const [ownerNameOptions, setOwnerNameOptions] = useState(normalizedNameSuggestions);
  const [ownerRoleOptions, setOwnerRoleOptions] = useState(normalizedRoleSuggestions);
  const [loadingOwnerNames, setLoadingOwnerNames] = useState(false);
  const [ownerNameError, setOwnerNameError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [ownerNames, setOwnerNames] = useState([]);
  const [ownerRoles, setOwnerRoles] = useState([]);

  useEffect(() => {
    if (!open) return;

    const nextInitializedKey = String(resetKey ?? "__default__");
    if (lastInitializedKeyRef.current === nextInitializedKey) return;

    lastInitializedKeyRef.current = nextInitializedKey;
    setOwnerNames(normalizedDefaultOwnerNames);
    setOwnerRoles(normalizedDefaultOwnerRoles);
    setSubmitError("");
  }, [normalizedDefaultOwnerNames, normalizedDefaultOwnerRoles, open, resetKey]);

  useEffect(() => {
    if (open) return;
    lastInitializedKeyRef.current = "";
    setSubmitting(false);
    setSubmitError("");
  }, [open]);

  useEffect(() => {
    setOwnerRoleOptions((prev) =>
      areTextArraysEqual(prev, normalizedRoleSuggestions)
        ? prev
        : normalizedRoleSuggestions
    );
  }, [normalizedRoleSuggestions]);

  useEffect(() => {
    setOwnerNameOptions((prev) =>
      areTextArraysEqual(prev, normalizedNameSuggestions)
        ? prev
        : normalizedNameSuggestions
    );
  }, [normalizedNameSuggestions]);

  useEffect(() => {
    if (!open) return;
    if (!loadRecruiterNameOptions) {
      recruiterUsersRef.current = [];
      setLoadingOwnerNames(false);
      setOwnerNameError("");
      setOwnerNameOptions(normalizedNameSuggestions);
      setOwnerRoleOptions(
        uniqText([
          ...DEFAULT_ROLE_OPTIONS,
          ...roleSuggestions,
          ...defaultOwnerRoles,
        ])
      );
      return undefined;
    }

    let active = true;
    setLoadingOwnerNames(true);
    setOwnerNameError("");

    fetchRecruiterUsers({ apiBase })
      .then((rows) => {
        if (!active) return;
        recruiterUsersRef.current = Array.isArray(rows) ? rows : [];
        setOwnerNameOptions(
          uniqText([
            ...normalizedNameSuggestions,
            ...recruiterUsersRef.current.map((row) => row?.name),
          ])
        );
        setOwnerRoleOptions(
          uniqText([
            ...DEFAULT_ROLE_OPTIONS,
            ...roleSuggestions,
            ...defaultOwnerRoles,
            ...recruiterUsersRef.current.map((row) => row?.role),
          ])
        );
      })
      .catch((error) => {
        if (!active) return;
        recruiterUsersRef.current = [];
        console.error("Failed to load recruiter users for owner dialog:", error);
        setOwnerNameOptions(normalizedNameSuggestions);
        setOwnerNameError(error?.message || nameLoadErrorText);
      })
      .finally(() => {
        if (!active) return;
        setLoadingOwnerNames(false);
      });

    return () => {
      active = false;
    };
  }, [
    apiBase,
    defaultOwnerRoles,
    loadRecruiterNameOptions,
    nameLoadErrorText,
    normalizedNameSuggestions,
    open,
    roleSuggestions,
  ]);

  const handleOwnerNamesChange = useCallback((nextValue = []) => {
    const nextOwnerNames = uniqText(nextValue).slice(-1);
    setOwnerNames(nextOwnerNames);

    const selectedOwnerName = nextOwnerNames[0] || "";
    const matchingUser = recruiterUsersRef.current.find(
      (row) => normalizeTextKey(row?.name) === normalizeTextKey(selectedOwnerName)
    );

    if (matchingUser?.role && !ownerRoles.length) {
      setOwnerRoles([normalizeText(matchingUser.role)]);
    }
  }, [ownerRoles.length]);

  const handleOwnerRolesChange = useCallback((nextValue = []) => {
    setOwnerRoles(uniqText(nextValue).slice(-1));
  }, []);

  const handleClose = useCallback(() => {
    if (submitting) return;
    onClose?.();
  }, [onClose, submitting]);

  const handleSubmit = useCallback(async () => {
    const ownerName = normalizeText(ownerNames[0]);
    const ownerRole = normalizeText(ownerRoles[0]);

    if (!ownerName) {
      setSubmitError(requiredNameErrorText);
      return;
    }
    if (!ownerRole) {
      setSubmitError(requiredRoleErrorText);
      return;
    }

    const matchingUser = recruiterUsersRef.current.find(
      (row) => normalizeTextKey(row?.name) === normalizeTextKey(ownerName)
    );

    try {
      setSubmitting(true);
      setSubmitError("");
      await onSubmit?.({
        ownerName,
        ownerRole,
        ownerUserId:
          matchingUser?.id != null && Number.isFinite(Number(matchingUser.id))
            ? Number(matchingUser.id)
            : null,
      });
      onClose?.();
    } catch (error) {
      console.error("Failed to save owner from owner dialog:", error);
      setSubmitError(error?.message || "Failed to save owner.");
    } finally {
      setSubmitting(false);
    }
  }, [
    onClose,
    onSubmit,
    ownerNames,
    ownerRoles,
    requiredNameErrorText,
    requiredRoleErrorText,
  ]);

  const resolvedHeaderIcon = headerIcon || <FigmaIcon name="popup-owner" />;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          width: "min(690px, calc(100vw - 32px))",
          maxWidth: "690px",
          borderRadius: "22px",
          boxShadow: "0 24px 70px rgba(15, 23, 42, 0.24)",
          m: 2,
        },
      }}
    >
      <Box sx={{ px: { xs: 2.25, sm: 4 }, py: { xs: 2.25, sm: 3.25 } }}>
        <Stack data-popup-header direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Stack direction="row" alignItems="center" spacing={1.3}>
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: "999px",
                display: "grid",
                placeItems: "center",
                color: "#3451F4",
                backgroundColor: "#F5F3FF",
              }}
            >
              {resolvedHeaderIcon}
            </Box>
            <Typography data-popup-title
              sx={{
                fontSize: 18,
                fontWeight: 600,
                color: "#101828",
                fontFamily: "Be Vietnam Pro",
              }}
            >
              {title}
            </Typography>
          </Stack>

          <IconButton data-popup-close
            onClick={handleClose}
            aria-label="Close"
            sx={{
              width: 32,
              height: 32,
              borderRadius: "999px",
              backgroundColor: PURPLE,
              color: "#FFFFFF",
              "&:hover": {
                backgroundColor: PURPLE,
                opacity: 0.92,
              },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Stack>

        {normalizedSummaryItems.length ? (
          <Box
            sx={{
              mt: 5,
              display: "flex",
              flexWrap: "wrap",
              gap: 0.8,
              alignItems: "center",
            }}
          >
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 600,
                color: BLUE,
                fontFamily: "Be Vietnam Pro",
              }}
            >
              Info:
            </Typography>
            {normalizedSummaryItems.map((item, index) => (
              <React.Fragment key={`${item}-${index}`}>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 500,
                    color: "#475467",
                    fontFamily: "Be Vietnam Pro",
                  }}
                >
                  {item}
                </Typography>
                {index < normalizedSummaryItems.length - 1 ? (
                  <Typography sx={{ fontSize: 13, color: "#D0D5DD" }}>|</Typography>
                ) : null}
              </React.Fragment>
            ))}
          </Box>
        ) : null}

        <Typography data-popup-section
          sx={{
            mt: 5,
            mb: 2,
            fontSize: 14,
            fontWeight: 700,
            color: BLUE,
            fontFamily: "Be Vietnam Pro",
          }}
        >
          {sectionTitle}
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
            gap: 3,
          }}
        >
          <Box>
            <Label>{nameLabel}</Label>
            <PopupMultiSelectField
              placeholder={namePlaceholder}
              options={ownerNameOptions}
              value={ownerNames}
              onChange={handleOwnerNamesChange}
              loading={loadingOwnerNames}
              splitFreeSoloOnPipe={false}
              textFieldProps={{
                sx: fieldSx,
              }}
              chipSx={chipSx}
              autocompleteProps={{
                forcePopupIcon: true,
              }}
            />
            {ownerNameError ? (
              <Typography sx={{ mt: 0.85, fontSize: 12, color: "#F04438" }}>
                {ownerNameError}
              </Typography>
            ) : null}
          </Box>

          <Box>
            <Label>{roleLabel}</Label>
            <PopupMultiSelectField
              chipsBelow={roleChipsBelow}
              placeholder={rolePlaceholder}
              options={ownerRoleOptions}
              value={ownerRoles}
              onChange={handleOwnerRolesChange}
              splitFreeSoloOnPipe={false}
              textFieldProps={{
                sx: fieldSx,
              }}
              chipSx={chipSx}
              autocompleteProps={{
                forcePopupIcon: true,
              }}
            />
          </Box>
        </Box>

        {submitError ? (
          <Typography
            sx={{
              mt: 2.2,
              fontSize: 12.5,
              fontWeight: 500,
              color: "#F04438",
              fontFamily: "Be Vietnam Pro",
            }}
          >
            {submitError}
          </Typography>
        ) : null}

        <Box data-popup-footer-inline sx={{ display: "flex", justifyContent: "flex-end", mt: 5 }}>
          <Button
            type="button"
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting}
            sx={{
              minWidth: 146,
              height: 40,
              px: 2.5,
              borderRadius: "999px",
              textTransform: "none",
              fontSize: 14,
              fontWeight: 600,
              fontFamily: "Be Vietnam Pro",
              backgroundColor: PURPLE,
              border: `1px solid ${PURPLE}`,
              color: "#FFFFFF",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "#FFFFFF",
                color: PURPLE,
                borderColor: PURPLE,
                boxShadow: "none",
              },
              "&.Mui-disabled": {
                color: "#FFFFFF",
                backgroundColor: "#CBB0FF",
                borderColor: "#CBB0FF",
              },
            }}
          >
            {submitting ? "Saving..." : submitLabel}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}
